/* eslint-disable import/no-named-as-default */
import React from "react";
import { ButtonText } from "@loanbossinc/component-library";
import { useFlags } from "launchdarkly-react-client-sdk";

import CalculatedField from "./calculated-field";
import HardCodedField from "./hard-coded-field";
import LoanBossField from "./loan-boss-field";
import RentRollField from "./rent-roll-field";
import SourceFileField from "./source-file-field";
import { ColumnWrapper, PaddedWrapper, PaddedWrapperFullWidth, Row, Title, StyledButtonMultiple } from "./styles";
import { DSCR_TYPE_NAMES } from "../dscr-line-items-management/helpers";

export const CreateNewField = props => {
  const {
    currentSelectedFieldType,
    isEditing,
    isEditingLineItem,
    isSelectingField,
    isFromDscrFromFile,
    isFromLineItemManagement,
    isFromDebtYieldCalculation,
    setFieldSelections,
    setDrawerSelections,
    isFromFile,
    setShowLabeledButton,
    isStep2Screen,
    reportConfigs,
    isChoosingForAccountingIntegration,
    isAnythingWasUpdated,
    setRentRollFieldSelections,
    currentSectionName,
    setDscrLineItemSelections
  } = props;
  
  const { bar616EnableBalanceOverrideFieldType: enableBalanceOverrideField } = useFlags();

  const getSelectedType = () => {
    switch (currentSelectedFieldType) {
      case "calculated":
        return <CalculatedField isNew {...props} />;
      case "file":
        return <SourceFileField isNew {...props} />;
      case "finance_override":
        return <SourceFileField isNew isFinanceOverride {...props} />;
      case "hard-coded":
        return <HardCodedField isNew {...props} />;
      case "loanboss":
        return <LoanBossField isNew {...props} />;
      case "rentroll":
        return <RentRollField isNew {...props} />;
      default:
        return <></>;
    }
  };

  const FIELD_TYPES = [
    {
      label: "Manual Input",
      onClick: () => {
        setFieldSelections({ currentSelectedFieldType: "hard-coded" });
      },
      iconName: "code",
      selected: currentSelectedFieldType === "hard-coded",
      isUsableForAccountingIntegration: false
    },
    {
      label: "Captured from file",
      onClick: () => {
        setFieldSelections({ currentSelectedFieldType: "file" });
      },
      iconName: "description",
      selected: currentSelectedFieldType === "file",
      isUsableForAccountingIntegration: true
    },
    {
      label: "Derived from LoanBoss",
      onClick: () => {
        setFieldSelections({ currentSelectedFieldType: "loanboss" });
      },
      iconName: "computer",
      selected: currentSelectedFieldType === "loanboss",
      isUsableForAccountingIntegration: false
    },
    {
      label: "Calculated",
      onClick: () => {
        setFieldSelections({ currentSelectedFieldType: "calculated" });
      },
      iconName: "receipt",
      selected: currentSelectedFieldType === "calculated",
      isUsableForAccountingIntegration: true
    }
  ];

  if ((isChoosingForAccountingIntegration || (reportConfigs && reportConfigs.length)) && !isFromDscrFromFile) {
    FIELD_TYPES.push({
      label: "From rent roll",
      onClick: () => {
        setFieldSelections({ currentSelectedFieldType: "rentroll" });
      },
      iconName: "format_list_numbered",
      selected: currentSelectedFieldType === "rentroll",
      isUsableForAccountingIntegration: true
    });
  }
  
  if (enableBalanceOverrideField && !isFromDscrFromFile) {
    FIELD_TYPES.push(
      {
        label: "Cashflow override",
        onClick: () => {
          setFieldSelections({ currentSelectedFieldType: "finance_override" });
        },
        iconName: "account_balance",
        selected: currentSelectedFieldType === "finance_override",
        isUsableForAccountingIntegration: false
      }
    );
  }

  const backToList = () => {
    if(isFromDscrFromFile) {
      setDrawerSelections({ currentSectionName: "dscr-line-items" });
      const fromFileDscrType = {type: DSCR_TYPE_NAMES.FROM_FILE};
      setDscrLineItemSelections({selectedDscrType: fromFileDscrType});
    }
    else if (isFromLineItemManagement) {
      const newState = {currentSectionName: "line-item-management"};
      if (isEditingLineItem) {
        newState.isEditingLineItem = true;
      }
      setDrawerSelections(newState);
    } else if (isFromDebtYieldCalculation) {
      setDrawerSelections({ currentSectionName: "debt-yield-line-items" });
    } else if (currentSectionName === "field-management" && isAnythingWasUpdated) {
      // we should display confirmation modal first
      setRentRollFieldSelections({ showConfirmationModal: true, locationModalIsOpenFrom: "go_back_to_fields" });
    } else {
      setFieldSelections({ isAdding: false, isEditing: false, selectedField: null });
    }

    if (isFromFile) {
      setShowLabeledButton(false);
    }
  };

  const filteredFieldTypes = isChoosingForAccountingIntegration ? FIELD_TYPES.filter(fieldType => fieldType.isUsableForAccountingIntegration) : FIELD_TYPES;

  const getBackTitle = () => {
    if (isEditingLineItem) {
      return "Go back";
    }

    return isEditing ? "Edit a field" : "Add a field";
  };

  return (
    <Row id="add-new-field-row" borderBottom="none">
      <PaddedWrapperFullWidth padding="0">
        {!isSelectingField && !isStep2Screen && (
          <ColumnWrapper>
            <PaddedWrapper padding="0">
              <ButtonText id="back-button" onClick={backToList} label="" reverse iconName="arrow_forward" />
            </PaddedWrapper>
            <Title fontSize="16px" paddingTop="5px">
              {getBackTitle()}
            </Title>
          </ColumnWrapper>
        )}
        {!isSelectingField && !isEditing && !isFromDebtYieldCalculation && !isFromFile && !isStep2Screen && (
          <Row justifyContent="center">
            <PaddedWrapper padding="1em">
              <StyledButtonMultiple id="field-type-selector" options={filteredFieldTypes} />
            </PaddedWrapper>
          </Row>
        )}
        {currentSelectedFieldType && getSelectedType()}
      </PaddedWrapperFullWidth>
    </Row>
  );
};

export default CreateNewField;
